import { useState } from 'react';
import { BackHeader } from '../components/BackHeader';
import { 
  Package, Truck, CheckCircle, Clock, AlertCircle, Bell, 
  QrCode, Camera, MessageSquare, ChevronRight, Calendar,
  MapPin, User, Phone
} from 'lucide-react';

interface PackageItem {
  id: string;
  trackingNumber: string;
  carrier: 'amazon' | 'mercadolibre' | 'fedex' | 'dhl' | 'ups' | 'other';
  description: string;
  status: 'in-transit' | 'out-for-delivery' | 'delivered' | 'ready-for-pickup' | 'exception';
  estimatedDelivery: string;
  deliveredAt?: string;
  pickupCode?: string;
  size: 'small' | 'medium' | 'large';
  weight: string;
  photo?: string;
  requiresSignature: boolean;
  notifications: boolean;
}

const carriers = {
  amazon: { name: 'Amazon', color: 'bg-orange-500' },
  mercadolibre: { name: 'MercadoLibre', color: 'bg-yellow-500' },
  fedex: { name: 'FedEx', color: 'bg-purple-600' },
  dhl: { name: 'DHL', color: 'bg-yellow-600' },
  ups: { name: 'UPS', color: 'bg-amber-700' },
  other: { name: 'Otro', color: 'bg-slate-500' },
};

const statusConfig = {
  'in-transit': { label: 'En tránsito', color: 'bg-blue-500/20 text-blue-400', icon: Truck },
  'out-for-delivery': { label: 'En reparto', color: 'bg-yellow-500/20 text-yellow-400', icon: Truck },
  'delivered': { label: 'Entregado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  'ready-for-pickup': { label: 'Listo para recoger', color: 'bg-orange-500/20 text-orange-400', icon: Package },
  'exception': { label: 'Problema', color: 'bg-red-500/20 text-red-400', icon: AlertCircle },
};

export default function PackageTracker() {
  const [packages, setPackages] = useState<PackageItem[]>([
    {
      id: 'PKG-001',
      trackingNumber: 'AMZ-7829-1023',
      carrier: 'amazon',
      description: 'Cargador USB-C y cable',
      status: 'ready-for-pickup',
      estimatedDelivery: '2026-03-17',
      pickupCode: 'A-4521',
      size: 'small',
      weight: '0.5 kg',
      requiresSignature: false,
      notifications: true,
    },
    {
      id: 'PKG-002',
      trackingNumber: 'MLV-4455-8899',
      carrier: 'mercadolibre',
      description: 'Aspiradora robot',
      status: 'out-for-delivery',
      estimatedDelivery: '2026-03-17',
      size: 'large',
      weight: '4.2 kg',
      requiresSignature: true,
      notifications: true,
    },
    {
      id: 'PKG-003',
      trackingNumber: 'FDX-7723-1100',
      carrier: 'fedex',
      description: 'Documentos importantes',
      status: 'delivered',
      estimatedDelivery: '2026-03-15',
      deliveredAt: '2026-03-15 14:30',
      size: 'small',
      weight: '0.2 kg',
      requiresSignature: true,
      notifications: false,
    },
    {
      id: 'PKG-004',
      trackingNumber: 'DHL-9988-2233',
      carrier: 'dhl',
      description: 'Ropa deportiva',
      status: 'in-transit',
      estimatedDelivery: '2026-03-20',
      size: 'medium',
      weight: '1.5 kg',
      requiresSignature: false,
      notifications: true,
    },
  ]);

  const [filter, setFilter] = useState<'all' | 'active' | 'delivered'>('active');
  const [selectedPackage, setSelectedPackage] = useState<PackageItem | null>(null);
  const [showQR, setShowQR] = useState(false);

  const filteredPackages = packages.filter(pkg => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['in-transit', 'out-for-delivery', 'ready-for-pickup'].includes(pkg.status);
    if (filter === 'delivered') return pkg.status === 'delivered';
    return true;
  });

  const activeCount = packages.filter(p => ['in-transit', 'out-for-delivery', 'ready-for-pickup'].includes(p.status)).length;
  const readyForPickupCount = packages.filter(p => p.status === 'ready-for-pickup').length;
  const deliveredCount = packages.filter(p => p.status === 'delivered').length;

  const handleMarkPickedUp = (packageId: string) => {
    setPackages(packages.map(pkg => 
      pkg.id === packageId ? { ...pkg, status: 'delivered' as const, deliveredAt: new Date().toISOString() } : pkg
    ));
    setSelectedPackage(null);
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback='/home/dashboard' />
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-orange-600/20 to-yellow-600/20 p-4 rounded-xl border border-orange-500/30">
          <p className="text-orange-400 text-sm">Por llegar</p>
          <p className="text-3xl font-bold text-white mt-1">{activeCount}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-4 rounded-xl border border-blue-500/30">
          <p className="text-blue-400 text-sm">Listos para recoger</p>
          <p className="text-3xl font-bold text-white mt-1">{readyForPickupCount}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 p-4 rounded-xl border border-green-500/30">
          <p className="text-green-400 text-sm">Entregados (30 días)</p>
          <p className="text-3xl font-bold text-white mt-1">{deliveredCount}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('active')}
          className={`flex-1 py-3 rounded-lg font-medium transition ${
            filter === 'active' ? 'bg-blue-600 text-white' : 'bg-[var(--v-surface-1)] text-slate-400'
          }`}
        >
          Activos
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`flex-1 py-3 rounded-lg font-medium transition ${
            filter === 'all' ? 'bg-blue-600 text-white' : 'bg-[var(--v-surface-1)] text-slate-400'
          }`}
        >
          Todos
        </button>
        <button
          onClick={() => setFilter('delivered')}
          className={`flex-1 py-3 rounded-lg font-medium transition ${
            filter === 'delivered' ? 'bg-blue-600 text-white' : 'bg-[var(--v-surface-1)] text-slate-400'
          }`}
        >
          Entregados
        </button>
      </div>

      {/* Add Package Button */}
      <button className="w-full bg-[var(--v-surface-1)] hover:bg-slate-700 border-2 border-dashed border-slate-600 text-slate-400 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition">
        <Package className="w-5 h-5" />
        Agregar número de rastreo
      </button>

      {/* Packages List */}
      <div className="space-y-3">
        {filteredPackages.map((pkg) => {
          const StatusIcon = statusConfig[pkg.status].icon;
          const carrier = carriers[pkg.carrier];
          
          return (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] hover:border-blue-500/50 cursor-pointer transition"
            >
              <div className="flex items-start gap-4">
                {/* Carrier Badge */}
                <div className={`w-12 h-12 rounded-lg ${carrier.color} flex items-center justify-center flex-shrink-0`}>
                  <Package className="w-6 h-6 text-white" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-xs">{carrier.name} • {pkg.trackingNumber}</p>
                      <h4 className="text-white font-semibold truncate">{pkg.description}</h4>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[pkg.status].color}`}>
                      {statusConfig[pkg.status].label}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {pkg.status === 'delivered' 
                        ? `Entregado: ${pkg.deliveredAt}`
                        : `Llegada: ${pkg.estimatedDelivery}`
                      }
                    </span>
                    <span>•</span>
                    <span>{pkg.weight}</span>
                    {pkg.requiresSignature && (
                      <>
                        <span>•</span>
                        <span className="text-yellow-400">Requiere firma</span>
                      </>
                    )}
                  </div>

                  {pkg.status === 'ready-for-pickup' && pkg.pickupCode && (
                    <div className="mt-3 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                      <p className="text-orange-400 text-sm font-medium flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        Código de recolección: {pkg.pickupCode}
                      </p>
                    </div>
                  )}
                </div>

                <ChevronRight className="w-5 h-5 text-slate-500" />
              </div>
            </div>
          );
        })}

        {filteredPackages.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-16 h-16 mx-auto mb-4" />
            <p>No hay paquetes {filter === 'active' ? 'activos' : filter === 'delivered' ? 'entregados' : ''}</p>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-white font-medium">Notificaciones de paquetes</p>
              <p className="text-slate-400 text-sm">Recibe alertas cuando lleguen</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" defaultChecked />
            <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Package Detail Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-[var(--v-surface-3)] flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{carriers[selectedPackage.carrier].name}</p>
                <h3 className="text-xl font-bold text-white">{selectedPackage.description}</h3>
              </div>
              <button 
                onClick={() => setSelectedPackage(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status */}
              <div className={`p-4 rounded-xl ${statusConfig[selectedPackage.status].color}`}>
                <div className="flex items-center gap-3">
                  {(() => {
                    const StatusIcon = statusConfig[selectedPackage.status].icon;
                    return <StatusIcon className="w-6 h-6" />;
                  })()}
                  <div>
                    <p className="font-semibold">{statusConfig[selectedPackage.status].label}</p>
                    <p className="text-sm opacity-80">
                      {selectedPackage.status === 'delivered' 
                        ? `Entregado el ${selectedPackage.deliveredAt}`
                        : `Entrega estimada: ${selectedPackage.estimatedDelivery}`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Tracking Info */}
              <div className="bg-slate-700/50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Número de rastreo</span>
                  <span className="text-white font-mono">{selectedPackage.trackingNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Peso</span>
                  <span className="text-white">{selectedPackage.weight}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Tamaño</span>
                  <span className="text-white capitalize">{selectedPackage.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Requiere firma</span>
                  <span className="text-white">{selectedPackage.requiresSignature ? 'Sí' : 'No'}</span>
                </div>
              </div>

              {/* Pickup Code */}
              {selectedPackage.status === 'ready-for-pickup' && selectedPackage.pickupCode && (
                <div className="bg-gradient-to-r from-orange-600/20 to-yellow-600/20 p-6 rounded-xl border border-orange-500/30 text-center">
                  <p className="text-orange-400 text-sm mb-2">Código de recolección</p>
                  <p className="text-4xl font-bold text-white tracking-wider">{selectedPackage.pickupCode}</p>
                  <p className="text-slate-400 text-sm mt-2">Muestra este código en recepción</p>
                  
                  {/* QR Code Placeholder */}
                  <div 
                    onClick={() => setShowQR(true)}
                    className="mt-4 w-32 h-32 bg-white mx-auto rounded-lg flex items-center justify-center cursor-pointer hover:opacity-90"
                  >
                    <QrCode className="w-20 h-20 text-slate-900" />
                  </div>
                  <p className="text-slate-500 text-xs mt-2">Toca para ampliar</p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                {selectedPackage.status === 'ready-for-pickup' && (
                  <button
                    onClick={() => handleMarkPickedUp(selectedPackage.id)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
                  >
                    Marcar como recogido
                  </button>
                )}
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Notificar a recepción
                </button>
                <button className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium">
                  Ver historial de rastreo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {showQR && selectedPackage?.pickupCode && (
        <div 
          className="fixed inset-0 bg-slate-900/95 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
        >
          <div className="bg-white p-8 rounded-2xl text-center">
            <div className="w-64 h-64 bg-[var(--v-surface-1)] mx-auto rounded-lg flex items-center justify-center mb-4">
              <QrCode className="w-48 h-48 text-white" />
            </div>
            <p className="text-slate-900 text-2xl font-bold">{selectedPackage.pickupCode}</p>
            <p className="text-slate-500 mt-2">Muestra este código en recepción</p>
            <p className="text-slate-400 text-sm mt-4">Toca anywhere para cerrar</p>
          </div>
        </div>
      )}
    </div>
  );
}
